/**
 * 🚀 Debounce Hook für Firebase Updates
 * 
 * Verhindert dass bei jedem Slider-Move oder Texteingabe
 * sofort in die DB geschrieben wird.
 */

import { useRef, useCallback } from 'react';

/**
 * Debounced Callback Hook
 * @param {Function} callback - Die Funktion die gedebounced werden soll
 * @param {number} delay - Delay in ms (default: 300ms)
 * @returns {Function} - Debounced Funktion
 */
export const useDebouncedCallback = (callback, delay = 300) => {
    const timeoutRef = useRef(null);
    const lastArgsRef = useRef(null);

    const debouncedCallback = useCallback((...args) => {
        // Speichere die letzten Args für flush
        lastArgsRef.current = args;
        
        // Clearen des vorherigen Timeouts
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Setzen eines neuen Timeouts
        timeoutRef.current = setTimeout(() => {
            callback(...args);
            lastArgsRef.current = null;
        }, delay);
    }, [callback, delay]);

    // Cleanup beim Unmount
    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        lastArgsRef.current = null;
    }, []);

    // ✅ FLUSH: Führt pending callback SOFORT aus
    const flush = useCallback(() => {
        if (timeoutRef.current && lastArgsRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            callback(...lastArgsRef.current);
            lastArgsRef.current = null;
        }
    }, [callback]);

    // Return debounced function with cancel and flush methods
    debouncedCallback.cancel = cancel;
    debouncedCallback.flush = flush;

    return debouncedCallback;
};

/**
 * Immediate + Debounced Callback Hook
 * Führt sofort aus (für UI-Update), aber debounced für DB
 * 
 * @param {Function} immediateCallback - Wird sofort ausgeführt (z.B. State Update)
 * @param {Function} debouncedCallback - Wird debounced ausgeführt (z.B. DB Update)
 * @param {number} delay - Delay in ms
 * @returns {Function}
 */
export const useImmediateAndDebounced = (immediateCallback, debouncedCallback, delay = 300) => {
    const timeoutRef = useRef(null);

    const combinedCallback = useCallback((...args) => {
        // 1. Sofort ausführen (UI Update)
        if (immediateCallback) {
            immediateCallback(...args);
        }

        // 2. Debounced ausführen (DB Update)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (debouncedCallback) {
                debouncedCallback(...args);
            }
        }, delay);
    }, [immediateCallback, debouncedCallback, delay]);

    return combinedCallback;
};

export default useDebouncedCallback;

